import { API } from '../../utils/amplifyWrapper';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { toast } from 'react-toastify';

// ==================== FETCH PODCASTS (LIST) ====================

export const fetchPodcasts = async (org_id: string, integration_id: string) => {
    const response = await API.get('main', `/organisation/${org_id}/integration/${integration_id}/podcast`, {
        headers: {'Content-Type': "application/json"}
    });
    return response.data;
}

interface IUsePodcastsProps {
    org_id: string
    integration_id: string
    enabled?: boolean
}

export const usePodcasts = ({org_id, integration_id, enabled = true}: IUsePodcastsProps) => {
    return useQuery({
        queryKey: ['podcasts', org_id, integration_id],
        queryFn: () => fetchPodcasts(org_id, integration_id),
        enabled: !!(org_id && integration_id && enabled),
        staleTime: 2 * 60 * 1000, // 2 minutes - podcast list changes moderately
    })
}

// ==================== FETCH SINGLE PODCAST ====================

export const fetchPodcast = async (org_id: string, integration_id: string, podcast_id: string) => {
    const response = await API.get('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}`, {
        headers: {'Content-Type': "application/json"}
    });
    return response.data;
}

interface IUsePodcastProps {
    org_id: string
    integration_id: string
    podcast_id: string
    enabled?: boolean
}

export const usePodcast = ({org_id, integration_id, podcast_id, enabled = true}: IUsePodcastProps) => {
    return useQuery({
        queryKey: ['podcast', org_id, integration_id, podcast_id],
        queryFn: () => fetchPodcast(org_id, integration_id, podcast_id),
        enabled: !!(org_id && integration_id && podcast_id && enabled),
        staleTime: 5 * 60 * 1000, // 5 minutes - individual podcast data doesn't change often
    })
}

// ==================== FETCH PODCAST EPISODES (WITH PAGINATION) ====================

interface IFetchPodcastEpisodesParams {
    org_id: string
    integration_id: string
    podcast_id: string
    next_page_token?: { PK: string; SK: string } | null
}

export const fetchPodcastEpisodes = async ({org_id, integration_id, podcast_id, next_page_token}: IFetchPodcastEpisodesParams) => {
    let url = `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}/episodes`;
    
    if (next_page_token) {
        const token = `${next_page_token.PK.split('#')[1]}:${next_page_token.SK.split('#')[1]}`;
        url = `${url}?next_page_token=${encodeURIComponent(token)}`;
    }
    
    const response = await API.get('main', url, {
        headers: {'Content-Type': "application/json"}
    });
    
    return {
        data: response.data,
        nextPageToken: response.next_page_token
    };
}

interface IUsePodcastEpisodesProps {
    org_id: string
    integration_id: string
    podcast_id: string
    enabled?: boolean
}

export const usePodcastEpisodes = ({org_id, integration_id, podcast_id, enabled = true}: IUsePodcastEpisodesProps) => {
    return useInfiniteQuery({
        queryKey: ['podcast-episodes', org_id, integration_id, podcast_id],
        queryFn: ({ pageParam }) => fetchPodcastEpisodes({
            org_id,
            integration_id,
            podcast_id,
            next_page_token: pageParam
        }),
        enabled: !!(org_id && integration_id && podcast_id && enabled),
        staleTime: 1 * 60 * 1000, // 1 minute - episodes list can change frequently
        getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
    });
}

// ==================== CREATE PODCAST ====================

interface ICreatePodcastBody {
    title: string
    body: string
    author: string
    category: string
    lang_code: string
    poster_path?: string
    is_automatic?: boolean
    [key: string]: any
}

export const createPodcast = async ({ org_id, integration_id, body }: { org_id: string, integration_id: string, body: ICreatePodcastBody }) => {
    try {
        const response = await API.post('main', `/organisation/${org_id}/integration/${integration_id}/podcast`, {
            body,
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Podcast has been created successfully!');
        return response.data;
    } catch (error: any) {
        const msg = error?.response?.data?.message || "Something went wrong while creating the podcast.";
        toast.error(msg);
        throw error;
    }
}

interface IUseCreatePodcastProps {
    org_id: string
    integration_id: string
}

export const useCreatePodcast = ({ org_id, integration_id }: IUseCreatePodcastProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (body: ICreatePodcastBody) => createPodcast({ org_id, integration_id, body }),
        onSuccess: () => {
            // Invalidate podcasts list to refetch
            queryClient.invalidateQueries({ queryKey: ['podcasts', org_id, integration_id] });
        }
    });
}

// ==================== UPDATE PODCAST ====================

export const updatePodcast = async ({ org_id, integration_id, podcast_id, body }: { org_id: string, integration_id: string, podcast_id: string, body: any }) => {
    try {
        await API.put('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}`, {
            body,
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Podcast has been updated successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while updating the podcast.");
        throw error;
    }
}

interface IUseUpdatePodcastProps {
    org_id: string
    integration_id: string
    podcast_id: string
}

export const useUpdatePodcast = ({ org_id, integration_id, podcast_id }: IUseUpdatePodcastProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (body: any) => updatePodcast({ org_id, integration_id, podcast_id, body }),
        onSuccess: () => {
            // Invalidate both the podcast detail and podcasts list
            queryClient.invalidateQueries({ queryKey: ['podcast', org_id, integration_id, podcast_id] });
            queryClient.invalidateQueries({ queryKey: ['podcasts', org_id, integration_id] });
        }
    });
}

// ==================== DELETE PODCAST ====================

export const deletePodcast = async ({ org_id, integration_id, podcast_id }: { org_id: string, integration_id: string, podcast_id: string }) => {
    try {
        await API.del('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}`);
        toast.success('Podcast show has been deleted!');
    } catch (error: any) {
        toast.error("Something went wrong while deleting the podcast.");
        throw error;
    }
}

interface IUseDeletePodcastProps {
    org_id: string
    integration_id: string
}

export const useDeletePodcast = ({ org_id, integration_id }: IUseDeletePodcastProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (podcast_id: string) => deletePodcast({ org_id, integration_id, podcast_id }),
        onSuccess: () => {
            // Invalidate podcasts list to refetch
            queryClient.invalidateQueries({ queryKey: ['podcasts', org_id, integration_id] });
        }
    });
}

// ==================== PUBLISH PODCAST ====================

export const publishPodcast = async ({ org_id, integration_id, podcast_id }: { org_id: string, integration_id: string, podcast_id: string }) => {
    try {
        await API.put('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}/publish/publish_channel`, {
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Show has been published successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while publishing the podcast.");
        throw error;
    }
}

interface IUsePublishPodcastProps {
    org_id: string
    integration_id: string
}

export const usePublishPodcast = ({ org_id, integration_id }: IUsePublishPodcastProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (podcast_id: string) => publishPodcast({ org_id, integration_id, podcast_id }),
        onSuccess: (_, podcast_id) => {
            // Invalidate both the podcast detail and podcasts list
            queryClient.invalidateQueries({ queryKey: ['podcast', org_id, integration_id, podcast_id] });
            queryClient.invalidateQueries({ queryKey: ['podcasts', org_id, integration_id] });
        }
    });
}

// ==================== UNPUBLISH PODCAST ====================

export const unpublishPodcast = async ({ org_id, integration_id, podcast_id }: { org_id: string, integration_id: string, podcast_id: string }) => {
    try {
        await API.put('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}/publish/unpublish`, {
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Show has been unpublished successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while unpublishing the podcast.");
        throw error;
    }
}

interface IUseUnpublishPodcastProps {
    org_id: string
    integration_id: string
}

export const useUnpublishPodcast = ({ org_id, integration_id }: IUseUnpublishPodcastProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (podcast_id: string) => unpublishPodcast({ org_id, integration_id, podcast_id }),
        onSuccess: (_, podcast_id) => {
            // Invalidate both the podcast detail and podcasts list
            queryClient.invalidateQueries({ queryKey: ['podcast', org_id, integration_id, podcast_id] });
            queryClient.invalidateQueries({ queryKey: ['podcasts', org_id, integration_id] });
        }
    });
}

// ==================== PUBLISH EPISODE ====================

export const publishEpisode = async ({ org_id, integration_id, podcast_id, content_id }: { org_id: string, integration_id: string, podcast_id: string, content_id: string }) => {
    try {
        await API.put('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}/episode/${content_id}/publish/publish`, {
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Episode has been published successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while publishing the episode.");
        throw error;
    }
}

interface IUsePublishEpisodeProps {
    org_id: string
    integration_id: string
    podcast_id: string
}

export const usePublishEpisode = ({ org_id, integration_id, podcast_id }: IUsePublishEpisodeProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (content_id: string) => publishEpisode({ org_id, integration_id, podcast_id, content_id }),
        onSuccess: () => {
            // Invalidate episodes list to refetch
            queryClient.invalidateQueries({ queryKey: ['podcast-episodes', org_id, integration_id, podcast_id] });
        }
    });
}

// ==================== UNPUBLISH EPISODE ====================

export const unpublishEpisode = async ({ org_id, integration_id, podcast_id, content_id }: { org_id: string, integration_id: string, podcast_id: string, content_id: string }) => {
    try {
        await API.put('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}/episode/${content_id}/publish/unpublish`, {
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Episode has been unpublished successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while unpublishing the episode.");
        throw error;
    }
}

interface IUseUnpublishEpisodeProps {
    org_id: string
    integration_id: string
    podcast_id: string
}

export const useUnpublishEpisode = ({ org_id, integration_id, podcast_id }: IUseUnpublishEpisodeProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (content_id: string) => unpublishEpisode({ org_id, integration_id, podcast_id, content_id }),
        onSuccess: () => {
            // Invalidate episodes list to refetch
            queryClient.invalidateQueries({ queryKey: ['podcast-episodes', org_id, integration_id, podcast_id] });
        }
    });
}

// ==================== DELETE EPISODE ====================

export const deleteEpisode = async ({ org_id, integration_id, podcast_id, content_id }: { org_id: string, integration_id: string, podcast_id: string, content_id: string }) => {
    try {
        await API.del('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}/episode/${content_id}`);
        toast.success('Episode has been deleted successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while deleting the episode.");
        throw error;
    }
}

interface IUseDeleteEpisodeProps {
    org_id: string
    integration_id: string
    podcast_id: string
}

export const useDeleteEpisode = ({ org_id, integration_id, podcast_id }: IUseDeleteEpisodeProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (content_id: string) => deleteEpisode({ org_id, integration_id, podcast_id, content_id }),
        onSuccess: () => {
            // Invalidate episodes list to refetch
            queryClient.invalidateQueries({ queryKey: ['podcast-episodes', org_id, integration_id, podcast_id] });
        }
    });
}

// ==================== UPDATE EPISODE SETTINGS ====================

export const updateEpisodeSettings = async ({ org_id, integration_id, podcast_id, content_id, body }: { org_id: string, integration_id: string, podcast_id: string, content_id: string, body: any }) => {
    try {
        const response = await API.put('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}/episode/${content_id}`, {
            body,
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Episode settings have been updated successfully!');
        return response.data;
    } catch (error: any) {
        toast.error("Something went wrong while updating episode settings.");
        throw error;
    }
}

interface IUseUpdateEpisodeSettingsProps {
    org_id: string
    integration_id: string
    podcast_id: string
    content_id: string
}

export const useUpdateEpisodeSettings = ({ org_id, integration_id, podcast_id, content_id }: IUseUpdateEpisodeSettingsProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (body: any) => updateEpisodeSettings({ org_id, integration_id, podcast_id, content_id, body }),
        onSuccess: () => {
            // Invalidate episodes list to refetch
            queryClient.invalidateQueries({ queryKey: ['podcast-episodes', org_id, integration_id, podcast_id] });
        }
    });
}

// ==================== ADD EPISODE TO PODCAST ====================

export const addEpisodeToPodcast = async ({ org_id, integration_id, podcast_id, content_id, body }: { org_id: string, integration_id: string, podcast_id: string, content_id: string, body: any }) => {
    try {
        const response = await API.post('main', `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}/content/${content_id}`, {
            body,
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Episode has been added to podcast successfully!');
        return response.data;
    } catch (error: any) {
        toast.error("Something went wrong while adding episode to podcast.");
        throw error;
    }
}

interface IUseAddEpisodeProps {
    org_id: string
    integration_id: string
    podcast_id: string
}

export const useAddEpisode = ({ org_id, integration_id, podcast_id }: IUseAddEpisodeProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: ({ content_id, ...body }: { content_id: string, [key: string]: any }) => 
            addEpisodeToPodcast({ org_id, integration_id, podcast_id, content_id, body }),
        onSuccess: () => {
            // Invalidate episodes list to refetch
            queryClient.invalidateQueries({ queryKey: ['podcast-episodes', org_id, integration_id, podcast_id] });
            // Also invalidate available content list
            queryClient.invalidateQueries({ queryKey: ['available-content', org_id, integration_id, podcast_id] });
        }
    });
}

// ==================== FETCH AVAILABLE CONTENT TO ADD TO PODCAST ====================

interface IFetchAvailableContentParams {
    org_id: string
    integration_id: string
    podcast_id: string
    next_page_token?: string | null
}

export const fetchAvailableContent = async ({org_id, integration_id, podcast_id, next_page_token}: IFetchAvailableContentParams) => {
    let url = `/organisation/${org_id}/integration/${integration_id}/podcast/${podcast_id}/episodes-add`;
    
    if (next_page_token) {
        url = `${url}?next_page_token=${encodeURIComponent(next_page_token)}`;
    }
    
    const response = await API.get('main', url, {
        headers: {'Content-Type': "application/json"}
    });
    
    return {
        data: response.data,
        nextPageToken: response.next_page_token
    };
}

interface IUseAvailableContentProps {
    org_id: string
    integration_id: string
    podcast_id: string
    enabled?: boolean
}

export const useAvailableContent = ({org_id, integration_id, podcast_id, enabled = true}: IUseAvailableContentProps) => {
    return useInfiniteQuery({
        queryKey: ['available-content', org_id, integration_id, podcast_id],
        queryFn: ({ pageParam }) => fetchAvailableContent({
            org_id,
            integration_id,
            podcast_id,
            next_page_token: pageParam
        }),
        enabled: !!(org_id && integration_id && podcast_id && enabled),
        staleTime: 1 * 60 * 1000, // 1 minute
        getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
    });
}

